/**
 * Data plane — Sources (L0) API client.
 *
 * Mirrors `backend/src/hub/routes/sources.py` (ADR-0026). A Source is a
 * registered connection: `internal` (Tray's own Postgres — one seeded,
 * non-deletable `is_system` row) or `external` (a relational DB via a
 * credential). Read-only by default.
 */
import { api } from "./api";

export type SourceKind = "internal" | "external" | "file" | "gateway";

export interface Source {
  id: string;
  name: string;
  description: string | null;
  kind: SourceKind;
  credential_id: string | null;
  config: Record<string, unknown>;
  enabled: boolean;
  tags: string[];
  /** True for the seeded internal source — cannot be deleted. */
  is_system: boolean;
  last_used_at: string | null;
  last_used_for: string | null;
  /** Liveness (ADR-0027 B3.2): the scheduled probe's last verdict + when. */
  health_status: string;
  health_checked_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

/** Create makes an **external** source — the internal one is system-seeded. */
export interface SourceCreate {
  name: string;
  description?: string | null;
  credential_id: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
  tags?: string[];
}

export type SourceUpdate = Partial<{
  name: string;
  description: string | null;
  credential_id: string;
  config: Record<string, unknown>;
  enabled: boolean;
  tags: string[];
}>;

export interface SourceTestResult {
  ok: boolean;
  message: string;
  details: Record<string, unknown>;
}

export interface SourceColumnInfo {
  name: string;
  type: string;
}

export interface SourceIntrospectResult {
  items: string[];
  columns: SourceColumnInfo[];
}

export type IntrospectLevel = "schemas" | "tables" | "columns";

/** Dry-run of a file ingest (ADR-0027 B4): inferred columns + sample rows. */
export interface FilePreview {
  columns: SourceColumnInfo[];
  sample: Record<string, unknown>[];
  total: number;
  truncated: boolean;
  /** .xlsx worksheet names — empty for a CSV / single-sheet file (no picker). */
  sheets: string[];
  /** Which worksheet was parsed (null for a CSV). */
  sheet: string | null;
}

/** Column types an operator may FORCE on import, overriding inference (B4). */
export const OVERRIDE_TYPES = [
  "text",
  "integer",
  "number",
  "boolean",
  "date",
  "datetime",
] as const;

export const sourcesApi = {
  list: (params?: { kind?: SourceKind; enabled?: boolean; tag?: string[] }) =>
    api.get<Source[]>("/sources", { query: params }),

  get: (id: string) => api.get<Source>(`/sources/${id}`),

  create: (body: SourceCreate) => api.post<Source>("/sources", body),

  update: (id: string, body: SourceUpdate) => api.patch<Source>(`/sources/${id}`, body),

  remove: (id: string) => api.delete<void>(`/sources/${id}`),

  test: (id: string) => api.post<SourceTestResult>(`/sources/${id}/test`),

  introspect: (
    id: string,
    params: { level: IntrospectLevel; schema?: string; table?: string },
  ) => api.get<SourceIntrospectResult>(`/sources/${id}/introspect`, { query: params }),

  /** Dry-run parse of a CSV/Excel — columns + sample, nothing is created.
   *  `sheet` picks an .xlsx worksheet (default: first). */
  previewFile: (file: File, sheet?: string) => {
    const fd = new FormData();
    fd.append("file", file);
    if (sheet) fd.append("sheet", sheet);
    return api.upload<FilePreview>("/sources/preview-file", fd);
  },

  /** Upload a CSV/Excel → a `file` source + its 1:1 dataset (ADR-0027 B4).
   *  `columnTypes` ({column: type}) forces a column's type, overriding inference. */
  uploadFile: (params: {
    file: File;
    name?: string;
    tags?: string[];
    columnTypes?: Record<string, string>;
    /** .xlsx worksheet to ingest (default: first). */
    sheet?: string;
  }) => {
    const fd = new FormData();
    fd.append("file", params.file);
    if (params.name) fd.append("name", params.name);
    if (params.tags?.length) fd.append("tags", params.tags.join(","));
    if (params.columnTypes && Object.keys(params.columnTypes).length)
      fd.append("column_types", JSON.stringify(params.columnTypes));
    if (params.sheet) fd.append("sheet", params.sheet);
    return api.upload<Source>("/sources/upload", fd);
  },

  /** Re-import a new file into an existing `file` source (replace its rows). */
  reupload: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.upload<Source>(`/sources/${id}/reupload`, fd);
  },
};
